import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, faChartLine, faLink, faRocket, faTachometerAlt,
  faGlobe, faSearch, faEye, faArrowUp, faArrowDown, faMinus,
  faSpinner, faExclamationTriangle, faCheckCircle, faInfoCircle,
  faClock, faDesktop, faMobileAlt, faMapMarkerAlt, faChartBar,
  faLightbulb, faSync, faFilter, faDownload, faExpand, faCompress,
  faTrophy, faUserSecret, faShieldAlt, faBolt, faChartPie,
  faExternalLinkAlt, faCalendarAlt, faStar, faAward, faFireAlt,
  faBrain, faRobot, faSitemap, faCode, faServer, faGaugeHigh,
  faFileAlt, faTags, faWandMagicSparkles, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { 
  getAllClients, 
  getSEODashboardData,
  getClientSEOSummary,
  getClientSEOPages,
  getClientAIReadiness,
  getClientSEORecommendations,
  streamSEOAnalysisStatus,
  runSEOAnalysis
} from '../../services/api';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale,
  PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  ArcElement, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, Title, Tooltip, Legend, Filler
);

// Types
interface Client {
  id: string;
  name: string;
  website_url: string;
  status: string;
}

interface SEODashboardData {
  client_id: string;
  client_name: string;
  domain: string;
  timestamp: string;
  cache_metadata?: {
    cached_at: string;
    confidence_score: number;
    is_stale: boolean;
    data_source: string;
    refresh_available: boolean;
    last_refreshed: string | null;
  };
  insights?: Array<{
    type: 'trend' | 'opportunity' | 'warning' | 'achievement';
    priority: 'critical' | 'high' | 'medium' | 'low';
    title: string;
    description: string;
    impact_score: number;
    action_items?: string[];
  }>;
  data: {
    live_visitors: {
      current_visitors: number;
      visits_today: number;
      pageviews_today: number;
      avg_visit_duration: number;
      bounce_rate: number;
      traffic_sources: { [key: string]: number };
      top_pages: Array<{ url: string; views: number }>;
      devices: { [key: string]: number };
      locations: Array<{ country: string; visitors: number }>;
      estimated_monthly_visits?: number;
      organic_keywords?: number;
      organic_etv?: number;
      data_source?: string;
    };
    keyword_rankings: {
      total_keywords: number;
      avg_position: number;
      visibility_score: number;
      keywords: Array<{
        keyword: string;
        position: number;
        search_volume: number;
        difficulty: string;
        cpc: number;
        url: string;
        trend: string;
      }>;
    };
    backlinks: {
      total_backlinks: number;
      referring_domains: number;
      domain_rank: number;
      new_backlinks_30d: number;
      lost_backlinks_30d: number;
    };
    competitors: {
      competitors: Array<{
        domain: string;
        avg_position: number;
        organic_keywords: number;
        organic_traffic: number;
        visibility_score: number;
        common_keywords: number;
      }>;
    };
    geo_distribution?: {
      countries: Array<{ country: string; location_code?: number; keywords?: number; etv?: number }>;
    };
    page_speed: {
      desktop: { performance_score: number; lcp: number; fid: number; cls: number; };
      mobile: { performance_score: number; lcp: number; fid: number; cls: number; };
    };
    seo_summary: {
      total_pages: number;
      avg_seo_score: number;
      critical_issues: number;
      high_issues: number;
      last_analysis: string | null;
    };
  };
}

interface SEORecommendation {
  category: string;
  priority: string;
  title: string;
  description: string;
  impact: string;
  effort: string;
}

interface SEOPageAnalysis {
  page_url: string;
  page_title: string;
  overall_seo_score: number;
  content_quality_score: number;
  technical_score: number;
  ai_readiness_score: number;
  priority_level: 'low' | 'medium' | 'high' | 'critical';
  meta_tags: {
    title: { content: string; length: number; issues: string[] };
    description: { content: string; length: number; issues: string[] };
    keywords: { content: string[]; density: { [key: string]: number }; issues: string[] };
    h1_tags: string[];
    h2_tags: string[];
  };
  content_analysis: {
    word_count: number;
    readability_score: number;
    keyword_density: { [key: string]: number };
    missing_keywords: string[];
    content_gaps: string[];
  };
  technical_issues: {
    page_speed: number;
    mobile_friendly: boolean;
    https: boolean;
    canonical_url: string;
    robots_txt: boolean;
    sitemap: boolean;
  };
  recommendations: Array<{
    category: string;
    priority: string;
    title: string;
    description: string;
    impact: string;
    effort: string;
  }>;
  analyzed_at: string;
}

interface SEOSession {
  session_id: string;
  status: string;
  progress_percentage: number;
  pages_analyzed: number;
  pages_failed: number;
  error_message?: string;
  total_pages?: number;
}

type TabType = 'overview' | 'realtime' | 'pages' | 'recommendations' | 'technical' | 'ai-insights';

export default function SEOCenter() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [dashboardData, setDashboardData] = useState<SEODashboardData | null>(null);
  const [pageAnalyses, setPageAnalyses] = useState<SEOPageAnalysis[]>([]);
  const [recommendations, setRecommendations] = useState<SEORecommendation[]>([]);
  const [aiReadinessData, setAiReadinessData] = useState<any>(null);
  const [currentSession, setCurrentSession] = useState<SEOSession | null>(null);
  const [expandedPage, setExpandedPage] = useState<string | null>(null);
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const eventSourceRef = useRef<EventSource | null>(null);

  // Utility functions
  const formatNumber = (num: number | null | undefined, defaultValue = 0): string => {
    return typeof num === 'number' ? num.toLocaleString() : defaultValue.toLocaleString();
  };

  const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-brand-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 90) return 'bg-brand-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-brand-100 text-brand-600 border-brand-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return <FontAwesomeIcon icon={faArrowUp} className="text-brand-500" />;
    if (trend === 'down') return <FontAwesomeIcon icon={faArrowDown} className="text-red-500" />;
    return <FontAwesomeIcon icon={faMinus} className="text-gray-400" />;
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'achievement': return faTrophy;
      case 'opportunity': return faLightbulb;
      case 'warning': return faExclamationTriangle;
      case 'trend': return faChartLine;
      default: return faInfoCircle;
    }
  };

  const getInsightBgColor = (type: string) => {
    switch (type) {
      case 'achievement': return 'bg-brand-50 border-brand-200';
      case 'opportunity': return 'bg-brand-50 border-brand-200';
      case 'warning': return 'bg-orange-50 border-orange-200';
      case 'trend': return 'bg-brand-50 border-brand-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getInsightTextColor = (type: string) => {
    switch (type) {
      case 'achievement': return 'text-brand-700';
      case 'opportunity': return 'text-brand-600';
      case 'warning': return 'text-orange-700';
      case 'trend': return 'text-brand-700';
      default: return 'text-gray-700';
    }
  };

  // API calls
  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchAllData();
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const clientsData = await getAllClients();
      setClients(clientsData.filter((c: Client) => c.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      // Fetch real-time dashboard data
      await fetchDashboardData();
      
      // Fetch SEO analysis data if available
      await fetchSEOAnalysisData();
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDashboardData = async (forceRefresh = false) => {
    if (!selectedClient) return;
    
    try {
      const data = await getSEODashboardData(selectedClient, forceRefresh);
      setDashboardData(data);
      
      if (forceRefresh) {
        toast.success('Data refreshed successfully!', {
          position: 'bottom-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
      console.error('Dashboard error:', error);
    }
  };

  const fetchSEOAnalysisData = async () => {
    if (!selectedClient) return;
    
    try {
      // Fetch SEO summary
      const summary = await getClientSEOSummary(selectedClient);
      
      // Fetch page analyses
      const pages = await getClientSEOPages(selectedClient);
      setPageAnalyses(pages);
      
      // Fetch recommendations
      const recs = await getClientSEORecommendations(selectedClient);
      console.log('[DEBUG] Recommendations response:', recs);
      
      // Flatten the nested recommendation structure
      const flattenedRecommendations = (recs.recommendations || [])
        .filter((item: any) => {
          // Ensure item exists and has a valid recommendation object
          return item && 
                 typeof item === 'object' && 
                 item.recommendation && 
                 typeof item.recommendation === 'object' &&
                 item.recommendation.title; // Must have at least a title
        })
        .map((item: any) => ({
          title: item.recommendation.title,
          description: item.recommendation.description || 'No description available',
          impact: item.recommendation.impact || 'medium',
          effort: item.recommendation.effort || 'medium',
          category: item.recommendation.category || 'general',
          priority: item.priority_level || 'medium',
          page_url: item.page_url || '',
          page_title: item.page_title || 'Untitled Page',
          overall_seo_score: item.overall_seo_score || 0
        }));
      
      console.log('[DEBUG] Flattened recommendations:', flattenedRecommendations);
      // Ensure all recommendations are valid objects before setting state
      const validRecommendations = flattenedRecommendations.filter(
        (rec: any) => rec && typeof rec === 'object' && rec.title
      );
      setRecommendations(validRecommendations);
      
      // Fetch AI readiness
      const aiData = await getClientAIReadiness(selectedClient);
      setAiReadinessData(aiData);
      
    } catch (error) {
      console.error('Error fetching SEO analysis:', error);
    }
  };

  const handleRunAnalysis = async () => {
    if (!selectedClient) return;
    
    setIsAnalyzing(true);
    try {
      const response = await runSEOAnalysis(selectedClient);
      const sessionId = response.session_id;
      
      // Start streaming status updates
      const eventSource = streamSEOAnalysisStatus(sessionId, (data: SEOSession) => {
        setCurrentSession(data);
        
        if (data.status === 'completed') {
          toast.success('SEO analysis completed!');
          fetchSEOAnalysisData();
          setIsAnalyzing(false);
          setCurrentSession(null);
          eventSource.close();
        } else if (data.status === 'failed') {
          toast.error(data.error_message || 'Analysis failed');
          setIsAnalyzing(false);
          setCurrentSession(null);
          eventSource.close();
        }
      });
      
      eventSourceRef.current = eventSource;
    } catch (error) {
      toast.error('Failed to start SEO analysis');
      setIsAnalyzing(false);
    }
  };

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    await fetchDashboardData(true);
    setIsRefreshing(false);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: faGaugeHigh },
    { id: 'realtime', label: 'Real-time', icon: faChartLine },
    { id: 'pages', label: 'Page Analysis', icon: faFileAlt },
    { id: 'recommendations', label: 'Recommendations', icon: faLightbulb },
    { id: 'technical', label: 'Technical', icon: faCode },
    { id: 'ai-insights', label: 'AI Insights', icon: faBrain }
  ];

  // Get real insights from backend data
  const getInsights = () => {
    console.log('[DEBUG] dashboardData:', dashboardData);
    console.log('[DEBUG] dashboardData.insights:', dashboardData?.insights);
    if (!dashboardData?.insights) return [];
    return dashboardData.insights;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FontAwesomeIcon icon={faSearch} className="text-brand-500" />
            SEO Center
          </h1>
          <p className="text-gray-600 mt-2">Comprehensive SEO analysis and optimization tools</p>
        </div>

        {/* Client Selector */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Client
              </label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">Choose a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedClient && (
              <div className="flex gap-3 ml-6">
                <button
                  onClick={handleRefreshData}
                  disabled={isRefreshing}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faSync} className={isRefreshing ? 'animate-spin' : ''} />
                  Refresh Data
                </button>
                <button
                  onClick={handleRunAnalysis}
                  disabled={isAnalyzing}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={isAnalyzing ? faSpinner : faRobot} className={isAnalyzing ? 'animate-spin' : ''} />
                  Run Deep Analysis
                </button>
              </div>
            )}
          </div>

          {/* Analysis Progress */}
          {currentSession && (
            <div className="mt-4 p-4 bg-brand-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-brand-600">
                  Analyzing SEO... {currentSession.pages_analyzed} of {currentSession.total_pages || '?'} pages
                </span>
                <span className="text-sm text-brand-500">
                  {currentSession.progress_percentage}%
                </span>
              </div>
              <div className="w-full bg-brand-200 rounded-full h-2">
                <div
                  className="bg-black h-2 rounded-full transition-all duration-300"
                  style={{ width: `${currentSession.progress_percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Cache Indicator */}
          {dashboardData?.cache_metadata && (
            <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
              <span>
                Data freshness: {dashboardData.cache_metadata.confidence_score.toFixed(0)}%
                {dashboardData.cache_metadata.is_stale && ' (Stale)'}
              </span>
              <span>
                Last updated: {new Date(dashboardData.cache_metadata.cached_at).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {!selectedClient ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FontAwesomeIcon icon={faSearch} className="text-6xl text-gray-300 mb-4" />
            <p className="text-gray-600">Select a client to view their SEO data</p>
          </div>
        ) : isLoading ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-brand-500 animate-spin mb-4" />
            <p className="text-gray-600">Loading SEO data...</p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="bg-white rounded-t-xl shadow-sm border-b">
              <div className="flex space-x-8 px-6 overflow-x-auto">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2
                      ${activeTab === tab.id
                        ? 'border-brand-500 text-brand-500'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <FontAwesomeIcon icon={tab.icon} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-b-xl shadow-sm p-6">
              {activeTab === 'overview' && dashboardData && (
                <div className="space-y-6">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <FontAwesomeIcon icon={faGlobe} className="text-3xl text-brand-500" />
                        <span className="text-sm font-medium text-brand-500">Overall Health</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        {dashboardData.data.seo_summary.avg_seo_score.toFixed(0)}%
                      </p>
                      <p className="text-sm text-gray-600 mt-1">SEO Score</p>
                    </div>

                    <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <FontAwesomeIcon icon={faChartLine} className="text-3xl text-brand-600" />
                        <span className="text-sm font-medium text-brand-600">Rankings</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        {formatNumber(dashboardData.data.keyword_rankings.total_keywords)}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Keywords Tracked</p>
                    </div>

                    <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <FontAwesomeIcon icon={faLink} className="text-3xl text-brand-600" />
                        <span className="text-sm font-medium text-brand-600">Authority</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        {formatNumber(dashboardData.data.backlinks.referring_domains)}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Referring Domains</p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl text-orange-600" />
                        <span className="text-sm font-medium text-orange-600">Issues</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        {dashboardData.data.seo_summary.critical_issues + dashboardData.data.seo_summary.high_issues}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Need Attention</p>
                    </div>
                  </div>

                  {/* Charts Section */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Traffic Trend Chart */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Traffic Overview</h3>
                      <div className="h-64">
                        <Line
                          data={{
                            labels: ['Visits Today', 'Pageviews Today'],
                            datasets: [{
                              label: 'Traffic',
                              data: [
                                dashboardData.data.live_visitors.visits_today || 0,
                                dashboardData.data.live_visitors.pageviews_today || 0
                              ],
                              borderColor: 'rgb(59, 130, 246)',
                              backgroundColor: 'rgba(64, 128, 242, 0.1)',
                              tension: 0.4
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false }
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Top Issues Distribution */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Issue Distribution</h3>
                      <div className="h-64">
                        <Doughnut
                          data={{
                            labels: ['Critical', 'High', 'Medium', 'Low'],
                            datasets: [{
                              data: [
                                dashboardData.data.seo_summary.critical_issues,
                                dashboardData.data.seo_summary.high_issues,
                                0,
                                0
                              ],
                              backgroundColor: [
                                'rgb(239, 68, 68)',
                                'rgb(251, 146, 60)',
                                'rgb(251, 191, 36)',
                                'rgb(59, 130, 246)'
                              ]
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Competitor Analysis */}
                  {dashboardData.data.competitors && dashboardData.data.competitors.competitors && dashboardData.data.competitors.competitors.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Competitors</h3>
                      <div className="space-y-3">
                        {dashboardData.data.competitors.competitors.slice(0, 5).map((comp, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-gray-400" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{comp.domain}</p>
                                <p className="text-xs text-gray-500">Common Keywords: {formatNumber(comp.common_keywords)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-6 text-sm">
                              <span className="text-gray-700">KW: {formatNumber(comp.organic_keywords)}</span>
                              <span className="text-gray-700">Traffic: {formatNumber(comp.organic_traffic)}</span>
                              <span className="text-gray-700">Visibility: {formatNumber(comp.visibility_score)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Insights */}
                  {getInsights().length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Insights</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {getInsights().filter(insight => insight && typeof insight === 'object' && insight.title).slice(0, 4).map((insight, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg border ${getInsightBgColor(insight.type)}`}
                          >
                            <div className="flex items-start gap-3">
                              <FontAwesomeIcon
                                icon={getInsightIcon(insight.type)}
                                className={`text-xl ${getInsightTextColor(insight.type)}`}
                              />
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900">{insight.title}</h4>
                                <p className="text-sm text-gray-600 mt-1">{insight.description || 'No description'}</p>
                                {insight.impact_score && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Impact:</span>
                                    <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                                      <div
                                        className={`h-2 rounded-full ${getScoreBgColor(insight.impact_score)}`}
                                        style={{ width: `${insight.impact_score}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'realtime' && dashboardData && (
                <div className="space-y-6">
                  {/* Live Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <FontAwesomeIcon icon={faUsers} className="text-3xl text-brand-500" />
                        <span className="text-sm font-medium text-brand-500">Live Now</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        {formatNumber(dashboardData.data.live_visitors.current_visitors)}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Active Visitors</p>
                    </div>

                    <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <FontAwesomeIcon icon={faEye} className="text-3xl text-brand-600" />
                        <span className="text-sm font-medium text-brand-600">Today</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        {formatNumber(dashboardData.data.live_visitors.pageviews_today)}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Page Views</p>
                    </div>

                    <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <FontAwesomeIcon icon={faClock} className="text-3xl text-brand-600" />
                        <span className="text-sm font-medium text-brand-600">Engagement</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        {Math.floor(dashboardData.data.live_visitors.avg_visit_duration / 60)}:{String(dashboardData.data.live_visitors.avg_visit_duration % 60).padStart(2, '0')}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Avg. Duration</p>
                    </div>
                  </div>

                  {/* Traffic Sources & Top Pages */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Traffic Sources</h3>
                      <div className="space-y-3">
                        {Object.entries(dashboardData.data.live_visitors.traffic_sources).map(([source, value]) => (
                          <div key={source} className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 capitalize">{source}</span>
                            <div className="flex items-center gap-3">
                              <div className="w-32 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-black h-2 rounded-full"
                                  style={{ width: `${value}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-600 w-12 text-right">{value}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Pages</h3>
                      <div className="space-y-3">
                        {dashboardData.data.live_visitors.top_pages.slice(0, 5).map((page, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 truncate flex-1 mr-4">{page.url}</span>
                            <span className="text-sm font-medium text-gray-900">{formatNumber(page.views)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Device & Location Stats */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Breakdown</h3>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <FontAwesomeIcon icon={faDesktop} className="text-2xl text-gray-600 mb-2" />
                          <p className="text-2xl font-bold text-gray-900">{dashboardData.data.live_visitors.devices.desktop || 0}%</p>
                          <p className="text-xs text-gray-500">Desktop</p>
                        </div>
                        <div>
                          <FontAwesomeIcon icon={faMobileAlt} className="text-2xl text-gray-600 mb-2" />
                          <p className="text-2xl font-bold text-gray-900">{dashboardData.data.live_visitors.devices.mobile || 0}%</p>
                          <p className="text-xs text-gray-500">Mobile</p>
                        </div>
                        <div>
                          <FontAwesomeIcon icon={faDesktop} className="text-2xl text-gray-600 mb-2" />
                          <p className="text-2xl font-bold text-gray-900">{dashboardData.data.live_visitors.devices.tablet || 0}%</p>
                          <p className="text-xs text-gray-500">Tablet</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Locations</h3>
                      <div className="space-y-3">
                        {(dashboardData.data.geo_distribution?.countries?.length
                          ? dashboardData.data.geo_distribution.countries.map((c) => ({ country: c.country, visitors: c.etv || 0 }))
                          : dashboardData.data.live_visitors.locations
                        ).slice(0, 5).map((location, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                              <span className="text-sm text-gray-700">{location.country}</span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">{formatNumber(location.visitors)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'pages' && (
                <div className="space-y-6">
                  {/* Filter */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Page-by-Page Analysis</h3>
                    <select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="all">All Pages</option>
                      <option value="critical">Critical Issues</option>
                      <option value="high">High Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="low">Low Priority</option>
                    </select>
                  </div>

                  {/* Page List */}
                  {pageAnalyses.length === 0 ? (
                    <div className="text-center py-12">
                      <FontAwesomeIcon icon={faFileAlt} className="text-4xl text-gray-300 mb-4" />
                      <p className="text-gray-500">No page analysis data available.</p>
                      <p className="text-sm text-gray-400 mt-2">Run a deep SEO analysis to see detailed page insights.</p>
                      <button
                        onClick={handleRunAnalysis}
                        className="mt-4 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                      >
                        Run Analysis Now
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pageAnalyses
                        .filter(page => filterPriority === 'all' || page.priority_level === filterPriority)
                        .map((page) => (
                          <div key={page.page_url} className="bg-white border rounded-lg overflow-hidden">
                            <div
                              className="p-4 cursor-pointer hover:bg-gray-50"
                              onClick={() => setExpandedPage(expandedPage === page.page_url ? null : page.page_url)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">{page.page_title || 'Untitled Page'}</h4>
                                  <p className="text-sm text-gray-500 mt-1">{page.page_url}</p>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(page.priority_level)}`}>
                                    {page.priority_level.toUpperCase()}
                                  </span>
                                  <div className="text-right">
                                    <p className={`text-2xl font-bold ${getScoreColor(page.overall_seo_score)}`}>
                                      {page.overall_seo_score}
                                    </p>
                                    <p className="text-xs text-gray-500">SEO Score</p>
                                  </div>
                                  <FontAwesomeIcon
                                    icon={expandedPage === page.page_url ? faArrowUp : faArrowDown}
                                    className="text-gray-400"
                                  />
                                </div>
                              </div>
                            </div>

                            {expandedPage === page.page_url && (
                              <div className="border-t px-4 py-6 bg-gray-50">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                  {/* Scores */}
                                  <div>
                                    <h5 className="font-medium text-gray-900 mb-4">Score Breakdown</h5>
                                    <div className="space-y-3">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Content Quality</span>
                                        <div className="flex items-center gap-2">
                                          <div className="w-32 bg-gray-200 rounded-full h-2">
                                            <div
                                              className={`h-2 rounded-full ${getScoreBgColor(page.content_quality_score)}`}
                                              style={{ width: `${page.content_quality_score}%` }}
                                            />
                                          </div>
                                          <span className={`text-sm font-medium ${getScoreColor(page.content_quality_score)}`}>
                                            {page.content_quality_score}%
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Technical SEO</span>
                                        <div className="flex items-center gap-2">
                                          <div className="w-32 bg-gray-200 rounded-full h-2">
                                            <div
                                              className={`h-2 rounded-full ${getScoreBgColor(page.technical_score)}`}
                                              style={{ width: `${page.technical_score}%` }}
                                            />
                                          </div>
                                          <span className={`text-sm font-medium ${getScoreColor(page.technical_score)}`}>
                                            {page.technical_score}%
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">AI Readiness</span>
                                        <div className="flex items-center gap-2">
                                          <div className="w-32 bg-gray-200 rounded-full h-2">
                                            <div
                                              className={`h-2 rounded-full ${getScoreBgColor(page.ai_readiness_score)}`}
                                              style={{ width: `${page.ai_readiness_score}%` }}
                                            />
                                          </div>
                                          <span className={`text-sm font-medium ${getScoreColor(page.ai_readiness_score)}`}>
                                            {page.ai_readiness_score}%
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Meta Tags */}
                                  <div>
                                    <h5 className="font-medium text-gray-900 mb-4">Meta Tags</h5>
                                    <div className="space-y-3">
                                      <div>
                                        <p className="text-sm text-gray-600">Title ({page.meta_tags?.title?.length ?? 0} chars)</p>
                                        <p className="text-sm font-medium text-gray-900 mt-1">{page.meta_tags?.title?.content ?? 'No title'}</p>
                                        {(page.meta_tags?.title?.issues?.length ?? 0) > 0 && (
                                          <p className="text-xs text-red-600 mt-1">{(page.meta_tags?.title?.issues ?? []).join(', ')}</p>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-600">Description ({page.meta_tags?.description?.length ?? 0} chars)</p>
                                        <p className="text-sm text-gray-900 mt-1 line-clamp-2">{page.meta_tags?.description?.content ?? 'No description'}</p>
                                        {(page.meta_tags?.description?.issues?.length ?? 0) > 0 && (
                                          <p className="text-xs text-red-600 mt-1">{(page.meta_tags?.description?.issues ?? []).join(', ')}</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Recommendations */}
                                {page.recommendations && page.recommendations.length > 0 && (
                                  <div className="mt-6">
                                    <h5 className="font-medium text-gray-900 mb-3">Recommendations</h5>
                                    <div className="space-y-2">
                                      {page.recommendations.filter(rec => rec && typeof rec === 'object' && rec.title).slice(0, 3).map((rec, idx) => (
                                        <div key={idx} className="flex items-start gap-2">
                                          <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 mt-1" />
                                          <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{rec.title}</p>
                                            <p className="text-xs text-gray-600 mt-1">{rec.description || 'No description'}</p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'recommendations' && (
                <div className="space-y-6">
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">Debug: {recommendations.length} recommendations loaded</p>
                  </div>
                  {recommendations.length === 0 ? (
                    <div className="text-center py-12">
                      <FontAwesomeIcon icon={faLightbulb} className="text-4xl text-gray-300 mb-4" />
                      <p className="text-gray-500">No recommendations available yet.</p>
                      <p className="text-sm text-gray-400 mt-2">Run a deep SEO analysis to get personalized recommendations.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {Object.entries(
                        recommendations
                          .filter(rec => rec && typeof rec === 'object' && rec.category)
                          .reduce((acc, rec) => {
                            const category = rec.category || 'General';
                            if (!acc[category]) acc[category] = [];
                            acc[category].push(rec);
                            return acc;
                          }, {} as Record<string, SEORecommendation[]>)
                      ).map(([category, categoryRecs]: [string, SEORecommendation[]]) => (
                        <div key={category} className="bg-gray-50 rounded-xl p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <FontAwesomeIcon icon={faTags} className="text-brand-500" />
                            {category}
                          </h3>
                          <div className="space-y-3">
                            {categoryRecs.filter(rec => rec && typeof rec === 'object').map((rec: SEORecommendation, idx: number) => (
                              <div key={idx} className="bg-white rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-medium text-gray-900">{rec.title || 'No Title'}</h4>
                                    <p className="text-sm text-gray-600 mt-1">{rec.description || 'No Description'}</p>
                                    <div className="flex items-center gap-4 mt-3 text-xs">
                                      <span className="text-brand-600">Impact: {rec.impact || 'Unknown'}</span>
                                      <span className="text-brand-500">Effort: {rec.effort || 'Unknown'}</span>
                                    </div>
                                  </div>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(rec.priority || 'low')}`}>
                                    {rec.priority || 'low'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'technical' && dashboardData && (
                <div className="space-y-6">
                  {/* Core Web Vitals */}
                  {dashboardData.data.page_speed && dashboardData.data.page_speed.desktop && dashboardData.data.page_speed.mobile ? (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-6">Core Web Vitals</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Desktop Scores */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                            <FontAwesomeIcon icon={faDesktop} /> Desktop Performance
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between mb-1">
                                <span className="text-sm text-gray-600">Performance Score</span>
                                <span className={`text-sm font-medium ${getScoreColor(dashboardData.data.page_speed.desktop.performance_score)}`}>
                                  {dashboardData.data.page_speed.desktop.performance_score}/100
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${getScoreBgColor(dashboardData.data.page_speed.desktop.performance_score)}`}
                                  style={{ width: `${dashboardData.data.page_speed.desktop.performance_score}%` }}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">LCP</p>
                                <p className="text-sm font-medium">{dashboardData.data.page_speed.desktop.lcp ? (dashboardData.data.page_speed.desktop.lcp / 1000).toFixed(1) : '0.0'}s</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">FID</p>
                                <p className="text-sm font-medium">{dashboardData.data.page_speed.desktop.fid || 0}ms</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">CLS</p>
                                <p className="text-sm font-medium">{dashboardData.data.page_speed.desktop.cls?.toFixed(3) || '0.000'}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Mobile Scores */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                            <FontAwesomeIcon icon={faMobileAlt} /> Mobile Performance
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between mb-1">
                                <span className="text-sm text-gray-600">Performance Score</span>
                                <span className={`text-sm font-medium ${getScoreColor(dashboardData.data.page_speed.mobile.performance_score)}`}>
                                  {dashboardData.data.page_speed.mobile.performance_score}/100
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${getScoreBgColor(dashboardData.data.page_speed.mobile.performance_score)}`}
                                  style={{ width: `${dashboardData.data.page_speed.mobile.performance_score}%` }}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">LCP</p>
                                <p className="text-sm font-medium">{dashboardData.data.page_speed.mobile.lcp ? (dashboardData.data.page_speed.mobile.lcp / 1000).toFixed(1) : '0.0'}s</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">FID</p>
                                <p className="text-sm font-medium">{dashboardData.data.page_speed.mobile.fid || 0}ms</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">CLS</p>
                                <p className="text-sm font-medium">{dashboardData.data.page_speed.mobile.cls?.toFixed(3) || '0.000'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <p className="text-gray-500 text-center">Page speed data not available</p>
                    </div>
                  )}

                  {/* Technical SEO Issues */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical SEO Health</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <FontAwesomeIcon icon={faExclamationCircle} className="text-3xl text-red-600 mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{dashboardData.data.seo_summary.critical_issues}</p>
                        <p className="text-sm text-gray-600">Critical Issues</p>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl text-orange-600 mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{dashboardData.data.seo_summary.high_issues}</p>
                        <p className="text-sm text-gray-600">High Priority</p>
                      </div>
                      <div className="text-center p-4 bg-brand-50 rounded-lg">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-brand-600 mb-2" />
                        <p className="text-2xl font-bold text-gray-900">{dashboardData.data.seo_summary.total_pages}</p>
                        <p className="text-sm text-gray-600">Pages Analyzed</p>
                      </div>
                    </div>
                  </div>

                  {/* Crawlability & Indexing */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Crawlability & Indexing</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon icon={faRobot} className="text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Robots.txt</span>
                        </div>
                        <FontAwesomeIcon icon={faCheckCircle} className="text-brand-500" />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon icon={faSitemap} className="text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">XML Sitemap</span>
                        </div>
                        <FontAwesomeIcon icon={faCheckCircle} className="text-brand-500" />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon icon={faShieldAlt} className="text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">HTTPS</span>
                        </div>
                        <FontAwesomeIcon icon={faCheckCircle} className="text-brand-500" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'ai-insights' && (
                <div className="space-y-6">
                  {getInsights().length === 0 ? (
                    <div className="text-center py-12">
                      <FontAwesomeIcon icon={faBrain} className="text-6xl text-gray-300 mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Insights Available</h3>
                      <p className="text-gray-500">AI insights will appear here once we analyze your SEO data.</p>
                      <button
                        onClick={handleRunAnalysis}
                        className="mt-6 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 mx-auto"
                      >
                        <FontAwesomeIcon icon={faWandMagicSparkles} />
                        Generate AI Insights
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="bg-gradient-to-r from-brand-50 to-brand-50 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered SEO Insights</h3>
                        <p className="text-sm text-gray-600">
                          Our AI has analyzed your website and identified {getInsights().length} insights to improve your SEO performance.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        {getInsights().filter(insight => insight && typeof insight === 'object' && insight.title).map((insight, idx) => (
                          <div
                            key={idx}
                            className={`p-6 rounded-xl border-2 ${getInsightBgColor(insight.type)}`}
                          >
                            <div className="flex items-start gap-4">
                              <div className={`p-3 rounded-lg ${
                                insight.type === 'achievement' ? 'bg-brand-100' :
                                insight.type === 'opportunity' ? 'bg-brand-100' :
                                insight.type === 'warning' ? 'bg-orange-100' :
                                'bg-brand-100'
                              }`}>
                                <FontAwesomeIcon
                                  icon={getInsightIcon(insight.type)}
                                  className={`text-2xl ${getInsightTextColor(insight.type)}`}
                                />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="text-lg font-semibold text-gray-900">{insight.title}</h4>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(insight.priority)}`}>
                                    {insight.priority.toUpperCase()}
                                  </span>
                                </div>
                                <p className="text-gray-600 mb-4">{insight.description}</p>
                                
                                {insight.action_items && insight.action_items.length > 0 && (
                                  <div className="bg-white bg-opacity-50 rounded-lg p-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Recommended Actions:</h5>
                                    <ul className="space-y-2">
                                      {insight.action_items.map((action, actionIdx) => (
                                        <li key={actionIdx} className="flex items-start gap-2">
                                          <FontAwesomeIcon icon={faCheckCircle} className="text-brand-500 mt-0.5" />
                                          <span className="text-sm text-gray-700">{action}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {insight.impact_score && (
                                  <div className="mt-4">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm text-gray-600">Potential Impact</span>
                                      <span className={`text-sm font-medium ${getScoreColor(insight.impact_score)}`}>
                                        {insight.impact_score}%
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className={`h-2 rounded-full ${getScoreBgColor(insight.impact_score)}`}
                                        style={{ width: `${insight.impact_score}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* AI Readiness Score */}
                      {aiReadinessData && (
                        <div className="bg-gradient-to-br from-brand-50 to-pink-50 rounded-xl p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">AI & Voice Search Readiness</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="text-center">
                              <div className="relative inline-flex items-center justify-center mb-3">
                                <svg className="w-24 h-24 transform -rotate-90">
                                  <circle
                                    cx="48"
                                    cy="48"
                                    r="40"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="none"
                                    className="text-gray-200"
                                  />
                                  <circle
                                    cx="48"
                                    cy="48"
                                    r="40"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="none"
                                    strokeDasharray={`${(aiReadinessData.overall_score / 100) * 251} 251`}
                                    className="text-brand-600"
                                  />
                                </svg>
                                <span className="absolute text-2xl font-bold text-gray-900">
                                  {aiReadinessData.overall_score}%
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">Overall AI Readiness</p>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm text-gray-600">Structured Data</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-brand-600 h-2 rounded-full"
                                      style={{ width: `${aiReadinessData.structured_data_score}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium">{aiReadinessData.structured_data_score}%</span>
                                </div>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Content Clarity</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-pink-600 h-2 rounded-full"
                                      style={{ width: `${aiReadinessData.content_clarity_score}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium">{aiReadinessData.content_clarity_score}%</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">
                              <h4 className="font-medium text-gray-900 mb-2">Key Improvements:</h4>
                              <ul className="space-y-1">
                                {aiReadinessData.recommendations?.slice(0, 3).map((rec: string, idx: number) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-brand-500 mt-0.5" />
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}